import type { default as StackTraceyClass } from "stacktracey";

import { IS_DEV } from "@/__main__/constants.mjs";
import { devError } from "@/util/dev.mjs";

let StackTracey: Constructor<StackTraceyClass> = null;

const EXCLUDE_ERROR_NAMES = new Set([
  "NetworkError",
  "NotFoundError",
  "ModuleImportError",
]);

const EXCLUDE_ERROR_MESSAGES = /screenobject|failed to fetch/i;

async function loadStackTracey() {
  if (!StackTracey) {
    StackTracey = (await import("stacktracey")).default;

    const originalThirdParty = StackTracey.prototype.isThirdParty;

    StackTracey.prototype.isThirdParty = function (path, externalDomain) {
      return (
        externalDomain !== "src" && originalThirdParty(path, externalDomain)
      );
    };
  }
}

export interface ErrorDetail {
  callee?: string;
  fileName: string;
  path: string;
}

export default async function sourceMap(
  error: Error,
): Promise<[string[], ErrorDetail[]]> {
  if (!(error instanceof Error)) {
    devError("cannot resolve stack trace as the error was invalid", error);

    return [[`${error}`], []];
  }

  if (
    EXCLUDE_ERROR_NAMES.has(error.name) ||
    EXCLUDE_ERROR_MESSAGES.test(error.message)
  ) {
    const rawOutput = (error.stack ?? "").split("\n").slice(0, 3);
    return [rawOutput, []];
  }

  await loadStackTracey();

  const errorStack = error.stack
    .split("\n")
    .filter((line) => !!line)
    .map((line) => line.split(" line ", 2)[0])
    .join("\n");

  const stack = new StackTracey(errorStack);

  // There is currently a bug where it doesn't parse "async" correctly:
  // https://github.com/xpl/stacktracey/issues/15
  // This is why those odd requests with "async " in them happen...
  const mappedStack = await stack.cleanAsync();

  // Since chunks change all the time in dev, there's no point in caching
  // anything plus it consumes a lot of memory.
  if (IS_DEV) {
    // I have no idea why this definition doesn't work.
    // https://github.com/xpl/stacktracey/blob/142143adb240a10ef6d2566e43cdbf16927f2b15/stacktracey.d.ts#L41
    // @ts-ignore
    StackTracey.resetCache();
  }

  const detailsOutput = mappedStack.items.map(
    ({ callee, fileRelative, line, column }) => {
      return {
        callee,
        fileName: fileRelative,
        path: `${fileRelative}:${line}:${column}`,
      };
    },
  );

  const mappedOutput = detailsOutput.map(
    ({ callee, path }) => `${callee ? `${callee} - ` : ""}${path}`,
  );

  return [mappedOutput, detailsOutput];
}
